import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { take, takeUntil } from 'rxjs/operators';
import { CommunicationService } from '../../../communication/communication.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { GraphSelectorPackage } from '../../../models/data-structures/results/graph-selector-package.model';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GraphSelectorService implements OnDestroy {
  onDestroyNotifier = new EventEmitter();
  graphSavedNotifier = new EventEmitter();

  constructor(private commService: CommunicationService,
              private toastrService: ConfiguredToastrService,
              public translationService: AnessaTranslationService
  ) { }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  saveGraphData(pack: GraphSelectorPackage) {
  // saveGraphData(request: {}, plotFramework: string, imageName: string, plotlyComponent: PlotlyComponent) {
    let graphDataId = '';
    (this.commService.postRequest(pack.request, 'wsasapi/report/saveGraphData') as Observable<SaveGraphDataResponse>)
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(take(1))
      .subscribe(
        (data) => {
          const graphDatasArr = data['obj'].graphDatas;
          graphDataId = graphDatasArr[graphDatasArr.length - 1]._id;
          switch (pack.plotFramework) {
            case 'plotly':
              this.savePlotlyImage(graphDataId, pack);
              break;
            case 'ngx':
              this.saveImageBody(graphDataId, pack);
              break;
          }
        });
  }

  savePlotlyImage(graphDataId: string, pack: GraphSelectorPackage) {
    const options: Plotly.ToImgopts = { format: 'png', height: 1000, width: 600 };
    window['Plotly'].toImage(pack.plotlyComponent.thePlot.plotEl.nativeElement, options)
      .then(dataUri => {
          let blob;
          if (options.format === 'svg') {
            blob = new window.Blob([dataUri], {type: 'image/svg+xml;charset=utf-8'});
          } else {
            const encodedString = dataUri.split(',')[1];
            const binary = this.fixBinary(window.atob(encodedString));
            blob = new window.Blob([binary], {type: 'image/' + options.format});
          }
          this.storeBlobAsFile(blob, graphDataId, pack.imageName);
        }
      );
  }

  // Taken from https://bl.ocks.org/nolanlawson/0eac306e4dac2114c752
  fixBinary(b: string) {
    const len = b.length;
    const buf = new ArrayBuffer(len);
    const arr = new Uint8Array(buf);
    for (let i = 0; i < len; i++) {
      arr[i] = b.charCodeAt(i);
    }
    return buf;
  }

  private storeBlobAsFile(blob: any, graphDataId: string, imageName: string) {
    const file = new File(
      [blob],
      imageName + '.png',
      {
        type: 'image/png',
        lastModified: Date.now()
      }
    );
    const uploadDoneNotifier = new EventEmitter();
    this.commService.uploadImage(file, imageName, graphDataId, 'wsasapi/report/saveImage')
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeUntil(uploadDoneNotifier))
      .subscribe(
        data => {
          if (data && data === 100) {
            this.toastrService.success(this.translationService.get('SERVICE.GRAPH_SELECTOR.SAVE_SUCCESSFUL'));
            uploadDoneNotifier.emit();
            this.graphSavedNotifier.emit();
          }
        }
      );
  }

  private saveImageBody(graphDataId: string, pack: GraphSelectorPackage) {
    const element = this.getSVGElement(pack);
    if (element != null && element instanceof SVGElement) {
      this.prepareForCanvas(element, pack);
      let svgURL = new XMLSerializer().serializeToString(element);
      svgURL = svgURL.replace(new RegExp('<rect[^\>]*\>', 'g'), '');
      const img = new Image();
      const ctx = pack.canvasElement.nativeElement.getContext('2d');
      const canvasBind: HTMLCanvasElement = pack.canvasElement.nativeElement;
      canvasBind.width = pack.chartSize[0] * 2;
      canvasBind.height = (pack.chartSize[1]) * 2;
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvasBind.width, canvasBind.height);
        this.teardownAfterCanvas(element, pack);
        canvasBind.toBlob((blob) => {
          if (!blob) return;
          const file = new File(
            [blob],
            pack.imageName + '.png',
            {
              type: 'image/png',
              lastModified: Date.now()
            }
          );
          const uploadDoneNotifier = new EventEmitter();
          this.commService.uploadImage(file, pack.imageName, graphDataId, 'wsasapi/report/saveImage')
            .pipe(takeUntil(this.onDestroyNotifier))
            .pipe(takeUntil(uploadDoneNotifier))
            .subscribe(
              data => {
                if (data && data === 100) {
                  this.toastrService.success(this.translationService.get('SERVICE.GRAPH_SELECTOR.SAVE_SUCCESSFUL'));
                  uploadDoneNotifier.emit();
                  this.graphSavedNotifier.emit();
                }
              }
            );
        });
      };
      img.src = 'data:image/svg+xml; charset=utf8, ' + encodeURIComponent(svgURL);
    }
  }

  getSVGElement(pack: GraphSelectorPackage) {
    if (pack.chartElement && pack.imageName) {
      let chartElement = pack.chartElement.nativeElement.cloneNode(true);
      // search until chartElement is SVG
      while (chartElement && !(chartElement instanceof SVGElement)) {
        chartElement = chartElement.children[0];
      }

      // verify it is an SVG then return
      if (chartElement && chartElement instanceof SVGElement) {
        return chartElement;
      }
    }
    return null;
  }

  prepareForCanvas(svg: SVGElement, pack: GraphSelectorPackage) {
    if (!svg.parentElement) return;

    if (svg.children[0].tagName !== 'defs') {
      const sheets = document.styleSheets;
      let styleStr = '';
      Array.prototype.forEach.call(sheets, function (sheet) {
        try { // we need a try-catch block for external stylesheets that could be there...
          styleStr += Array.prototype.reduce.call(sheet.cssRules, function (a, b) {
            return a + b.cssText; // just concatenate all our cssRules' text
          }, '');
        } catch (e) {
          console.log(e);
        }
      });

      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      style.innerHTML = styleStr;
      svg.insertBefore(defs, svg.firstElementChild);
      defs.appendChild(style);
      // now append it in your svg node
    }

    for (let i = 0; i < svg.children.length; i++) {
      if (svg.children[i].tagName === 'foreignObject') {
        svg.removeChild(svg.children[i]);
      }
    }

    // Drawing legend for canvas
    // This is drawn just offscreen on the SVG so there's no need to hide it. The canvas is wider and will display it.
    svg.setAttribute('width', pack.chartSize[0].toString());
    const foreign = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
    let legendHeight = 0;
    const legendElement = svg.parentElement.getElementsByTagName('ngx-charts-legend');
    if (legendElement.length > 0) {
      pack.legendWidth = document.getElementsByTagName('ngx-charts-legend')[0].clientWidth;
      legendHeight = document.getElementsByTagName('ngx-charts-legend')[0].clientHeight;
      // tslint:disable-next-line:radix
      const graphHeight = parseInt(svg.getAttribute('height') ?? '');
      // increase the chart size to include legend
      pack.chartSize[1] = pack.chartSize[1] + legendHeight;
      foreign.setAttribute('width', pack.legendWidth.toString());
      foreign.setAttribute('height', legendHeight.toString());
      foreign.setAttribute('x', '5');
      foreign.setAttribute('y', graphHeight.toString());
      const legendOriginal = document.getElementsByTagName('ngx-charts-legend');
      const legend = legendOriginal[0].cloneNode(true);
      const tempDiv = document.createElement('div');
      tempDiv.appendChild(legend);
      foreign.innerHTML = '<div>Test Text</div>';
      foreign.innerHTML = tempDiv.innerHTML;

      // add legend to the image
      svg.appendChild(foreign);

      // add the height of the legend to the dimensions
      svg.setAttribute('height', (graphHeight + legendHeight).toString());
    }
  }

  teardownAfterCanvas(svg: SVGElement, pack: GraphSelectorPackage) {
    if (svg.children[0].tagName !== 'defs') {
      svg.removeChild(svg.children[0]);
    }
    for (let i = 0; i < svg.children.length; i++) {
      if (svg.children[i].tagName === 'foreignObject') {
        svg.removeChild(svg.children[i]);
      }
    }
    if (svg.parentElement && svg.parentElement.getElementsByTagName('ngx-charts-legend').length > 0) {
      svg.setAttribute('width', (pack.chartSize[0]).toString());
    }
  }
}

interface SaveGraphDataResponse {
  obj: {
    graphDatas: {
      _id: string,
      data: string,
      name: string
    }[]
  };
};